import { Link } from 'react-router-dom'
import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis } from 'recharts'
import { AlertTriangle, ArrowRight, CheckCircle2, ShieldAlert, Wrench } from 'lucide-react'
import { BAR_RADIUS, CHART, gridProps, tooltipProps, xAxisProps, yAxisProps } from '@/lib/chart'
import { reasonLabel } from '@/lib/issueReasons'
import type { IssueReportSummary } from '@/types'
import Card, { SectionLabel } from '@/components/ui/Card'
import Button from '@/components/ui/Button'
import StatTile from '@/components/ui/StatTile'
import { ChartBox, ChartEmpty } from '@/components/ui/Chart'
import { Table, Row, Cell, EmptyRow } from '@/components/ui/Table'

const icon = 'size-5'

export default function IssuesTab({ data, range }: { data: IssueReportSummary; range: { from: string; to: string } }) {
  const t = data.totals
  // Each section keeps its own presets and several may share a name, so the bar names the section.
  const reasons = data.by_reason
    .slice(0, 12)
    .map((row) => ({ ...row, label: row.id === null ? row.reason : reasonLabel(row.reason, row.department) }))
  const listHref = `/admin/issues?status=all&from=${range.from}&to=${range.to}`

  return (
    <>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatTile label="Issues flagged" value={t.flagged} icon={<AlertTriangle className={icon} />} tone={t.flagged > 0 ? 'danger' : 'neutral'} to={listHref} />
        <StatTile label="Still open" value={t.open} icon={<ShieldAlert className={icon} />} tone={t.open > 0 ? 'danger' : 'neutral'} to={`/admin/issues?status=open&from=${range.from}&to=${range.to}`} hint="nobody has acted yet" />
        <StatTile label="Resolved" value={t.resolved} icon={<CheckCircle2 className={icon} />} tone="success" to={`/admin/issues?status=resolved&from=${range.from}&to=${range.to}`} />
        <StatTile label="Cleared on the floor" value={t.cleared} icon={<Wrench className={icon} />} tone="neutral" to={`/admin/issues?status=cleared&from=${range.from}&to=${range.to}`} hint="work resumed after the flag" />
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <Card className="min-w-0">
          <SectionLabel>By reason</SectionLabel>
          {reasons.length ? (
            <ChartBox height={Math.max(160, reasons.length * 30 + 24)}>
              <BarChart data={reasons} layout="vertical" margin={{ left: 4, right: 16 }}>
                <CartesianGrid strokeDasharray="2 2" stroke={CHART.grid} horizontal={false} />
                <XAxis type="number" allowDecimals={false} {...xAxisProps} />
                <YAxis type="category" dataKey="label" width={170} tick={{ fontSize: 11, fill: CHART.tick }} axisLine={false} tickLine={false} />
                <Tooltip {...tooltipProps} />
                <Bar dataKey="count" name="Issues" fill={CHART.danger} radius={[0, 5, 5, 0]} />
              </BarChart>
            </ChartBox>
          ) : (
            <ChartEmpty height={160}>No issues flagged in this range.</ChartEmpty>
          )}
        </Card>

        <Card className="min-w-0">
          <SectionLabel>By section</SectionLabel>
          <ChartBox height={Math.max(160, reasons.length * 30 + 24)}>
            <BarChart data={data.by_department}>
              <CartesianGrid {...gridProps} />
              <XAxis dataKey="department" {...xAxisProps} tick={{ fontSize: 10, fill: CHART.tick }} interval={0} />
              <YAxis allowDecimals={false} {...yAxisProps} />
              <Tooltip {...tooltipProps} />
              <Bar dataKey="count" name="Issues" fill={CHART.danger} radius={BAR_RADIUS} />
            </BarChart>
          </ChartBox>
        </Card>
      </div>

      <Card className="mt-4 min-w-0">
        <SectionLabel>Issues per day</SectionLabel>
        <ChartBox height={192}>
          <BarChart data={data.daily}>
            <CartesianGrid {...gridProps} />
            <XAxis dataKey="label" {...xAxisProps} />
            <YAxis allowDecimals={false} {...yAxisProps} />
            <Tooltip {...tooltipProps} />
            <Bar dataKey="count" name="Issues" fill={CHART.danger} radius={BAR_RADIUS} />
          </BarChart>
        </ChartBox>
      </Card>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <SectionLabel className="mb-0">Operators who flagged the most</SectionLabel>
        <Link to={listHref}>
          <Button variant="outline">
            Open issue list <ArrowRight className="size-4" />
          </Button>
        </Link>
      </div>
      <Table className="mt-3" columns={['Operator', 'Department', 'Issues flagged']}>
        {data.by_operator.map((row) => (
          <Row key={row.operator + (row.department ?? '')}>
            <Cell className="font-semibold">{row.operator}</Cell>
            <Cell>{row.department ?? '-'}</Cell>
            <Cell className="font-semibold text-status-danger">{row.count}</Cell>
          </Row>
        ))}
        {data.by_operator.length === 0 && <EmptyRow colSpan={3}>No issues flagged in this date range.</EmptyRow>}
      </Table>
    </>
  )
}
